import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuthStatus, useLogin, useMe, useVerifyMfa } from '../api/hooks';
import { OtpField } from '../components/OtpField';
import { Btn, Field, Hint, Surface } from '../components/ui';
import { useT } from '../i18n';

export function Login() {
  const { data: me, isPending } = useMe();
  const { data: status } = useAuthStatus();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const t = useT();
  const login = useLogin(mode);
  const verify = useVerifyMfa();
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const navigate = useNavigate();

  if (!isPending && me) return <Navigate to="/" replace />;

  const err = login.error instanceof ApiError ? login.error : null;
  const verifyErr = verify.error instanceof ApiError ? verify.error : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: (r) => {
          if (r.mfaRequired && r.challenge) setChallenge(r.challenge);
          else navigate('/', { replace: true });
        },
      },
    );
  };
  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!challenge || verify.isPending) return;
    verify.mutate(
      { challenge, code },
      { onSuccess: () => navigate('/', { replace: true }), onError: () => setCode('') },
    );
  };

  if (challenge) {
    return (
      <div className="phone auth">
        <header id="topbar">
          <h1>{t('login.mfa.title')}</h1>
        </header>
        <main id="screen">
          <form onSubmit={submitCode}>
            <Surface className="pad mt">
              <OtpField
                label={t('login.mfa.code')}
                value={code}
                onChange={setCode}
                invalid={!!verifyErr && !code}
                recovery
                autoFocus
              />
              {verifyErr && <p className="form-error">{verifyErr.message}</p>}
              <Btn variant="primary" full type="submit" disabled={verify.isPending}>
                {t('login.submit')}
              </Btn>
            </Surface>
          </form>
          <Btn
            full
            onClick={() => {
              setChallenge(null);
              setCode('');
            }}
          >
            {t('login.mfa.back')}
          </Btn>
          <Hint>{t('login.mfa.hint')}</Hint>
        </main>
      </div>
    );
  }

  const registering = mode === 'register';
  return (
    <div className="phone auth">
      <header id="topbar">
        <h1>{registering ? t('login.register') : t('login.title')}</h1>
      </header>
      <main id="screen">
        <form onSubmit={submit}>
          <Surface className="pad mt">
            <Field
              label={t('login.email')}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={err?.fieldErrors.email}
              required
            />
            <Field
              label={t('login.password')}
              type="password"
              autoComplete={registering ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={err?.fieldErrors.password}
              required
            />
            {err && !Object.keys(err.fieldErrors).length && (
              <p className="form-error">{err.message}</p>
            )}
            <Btn variant="primary" full type="submit" disabled={login.isPending}>
              {registering ? t('login.create') : t('login.submit')}
            </Btn>
          </Surface>
        </form>
        {status?.registrationOpen && (
          <Btn full onClick={() => setMode(registering ? 'login' : 'register')}>
            {registering ? t('login.haveAccount') : t('login.create')}
          </Btn>
        )}
        <Hint>{t('login.hint')}</Hint>
      </main>
    </div>
  );
}
